import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';
import AppModal from '../../shared/AppModal';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import axios from '../../utils/axios';
import { helpDeskTickets, getSoftwareUsers } from '../../utils/urls';
import useDebounce from '../../components/apps/appsKanban/hooks/useDebounce';
import TicketDetailDialog from '../../components/helpDesk/TicketDetailDialog';
import {
    STATUS_OPTIONS,
    PRIORITY_OPTIONS,
    CATEGORY_OPTIONS,
    statusLabel,
    priorityLabel,
    categoryLabel,
    statusBadgeClass,
    priorityBadgeClass,
    formatDateTime,
    resolveUserName,
} from './helpDeskConstants';
import './helpDesk.css';

const EMPTY_CREATE_FORM = { title: '', description: '', priority: 'medium', category: 'other' };

export default function HelpDesk() {
    const userId = useSelector(state => state.user.userId);
    const isSuperuser = useSelector(state => state.user.isSuperuser);

    const toast = useRef(null);

    const [tickets, setTickets] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(15);

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [statusFilter, setStatusFilter] = useState(null);
    const [priorityFilter, setPriorityFilter] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState(null);

    const [usersList, setUsersList] = useState([]);

    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
    const [createErrors, setCreateErrors] = useState({});
    const [creating, setCreating] = useState(false);

    const [detailTicketId, setDetailTicketId] = useState(null);

    const fetchTickets = useCallback(() => {
        setLoading(true);
        const page = Math.floor(first / rows) + 1;
        const params = { page, page_size: rows };
        if (statusFilter) params.status = statusFilter;
        if (priorityFilter) params.priority = priorityFilter;
        if (categoryFilter) params.category = categoryFilter;
        if (debouncedSearch) params.search = debouncedSearch;

        axios.get(helpDeskTickets, { params })
            .then(res => {
                setTickets(res.data.results || []);
                setTotalRecords(res.data.count || 0);
            })
            .catch(() => {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load tickets', life: 3000 });
            })
            .finally(() => setLoading(false));
    }, [first, rows, statusFilter, priorityFilter, categoryFilter, debouncedSearch]);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    useEffect(() => { setFirst(0); }, [statusFilter, priorityFilter, categoryFilter, debouncedSearch]);

    useEffect(() => {
        axios.get(getSoftwareUsers)
            .then(res => {
                const options = res.data?.map(u => ({ label: u.full_name, value: u.id, image: u.image })) || [];
                setUsersList(options);
            })
            .catch(() => {});
    }, []);

    const openCreateDialog = () => {
        setCreateForm(EMPTY_CREATE_FORM);
        setCreateErrors({});
        setCreateOpen(true);
    };

    const setCreateField = (key, val) => {
        setCreateForm(prev => ({ ...prev, [key]: val }));
        if (createErrors[key]) setCreateErrors(prev => ({ ...prev, [key]: undefined }));
    };

    const validateCreate = () => {
        const errs = {};
        if (!createForm.title.trim()) errs.title = 'Required';
        return errs;
    };

    const handleCreate = () => {
        const errs = validateCreate();
        if (Object.keys(errs).length) { setCreateErrors(errs); return; }

        setCreating(true);
        axios.post(helpDeskTickets, {
            title: createForm.title.trim(),
            description: createForm.description.trim() || undefined,
            priority: createForm.priority,
            category: createForm.category,
        })
            .then(res => {
                toast.current?.show({ severity: 'success', summary: 'Ticket Created', detail: `#${res.data.id} ${res.data.title}`, life: 3000 });
                setCreateOpen(false);
                fetchTickets();
            })
            .catch(err => {
                const msg = err?.response?.data?.detail || 'Failed to create ticket';
                toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
            })
            .finally(() => setCreating(false));
    };

    // ── Column templates ───────────────────────────────────────────────
    const titleCol = (row) => (
        <div className="hd-title-cell">
            <div className="hd-title">{row.title}</div>
            <div className="hd-sub">#{row.id}</div>
        </div>
    );

    const statusCol = (row) => <span className={statusBadgeClass(row.status)}>{statusLabel(row.status)}</span>;
    const priorityCol = (row) => <span className={priorityBadgeClass(row.priority)}>{priorityLabel(row.priority)}</span>;
    const categoryCol = (row) => <span className="hd-mono">{categoryLabel(row.category)}</span>;
    const assignedCol = (row) => <span>{resolveUserName(row.assigned_to, usersList)}</span>;
    const createdCol = (row) => <span style={{ fontSize: 13 }}>{formatDateTime(row.created_at)}</span>;

    const actionsCol = (row) => (
        <button className="hd-btn hd-btn-ghost hd-btn-sm" onClick={() => setDetailTicketId(row.id)}>
            <i className="pi pi-eye" /> View
        </button>
    );

    const dialogFooter = (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="hd-btn hd-btn-secondary" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</button>
            <button className="hd-btn hd-btn-primary" onClick={handleCreate} disabled={creating}>
                {creating ? <><i className="pi pi-spin pi-spinner" /> Creating…</> : <><i className="pi pi-plus" /> Create Ticket</>}
            </button>
        </div>
    );

    return (
        <div className="hd-root">
            <Toast ref={toast} />

            <div className="hd-header">
                <div>
                    <h1 className="hd-title-h1">Help Desk</h1>
                    <p className="hd-subtitle">Track and manage support tickets</p>
                </div>
                <button className="hd-btn hd-btn-primary hd-btn-lg" onClick={openCreateDialog}>
                    <i className="pi pi-plus" /> New Ticket
                </button>
            </div>

            <div className="hd-table-wrap">
                <div className="hd-table-toolbar">
                    <div className="hd-search-wrap">
                        <i className="pi pi-search hd-search-icon" />
                        <InputText
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Search tickets…"
                            className="hd-search"
                        />
                    </div>

                    <div className="hd-filters">
                        <Dropdown
                            value={statusFilter}
                            options={STATUS_OPTIONS}
                            onChange={e => setStatusFilter(e.value)}
                            placeholder="All Statuses"
                            showClear
                            className="hd-filter-dd"
                            panelClassName="hd-dd-panel"
                        />
                        <Dropdown
                            value={priorityFilter}
                            options={PRIORITY_OPTIONS}
                            onChange={e => setPriorityFilter(e.value)}
                            placeholder="All Priorities"
                            showClear
                            className="hd-filter-dd"
                            panelClassName="hd-dd-panel"
                        />
                        <Dropdown
                            value={categoryFilter}
                            options={CATEGORY_OPTIONS}
                            onChange={e => setCategoryFilter(e.value)}
                            placeholder="All Categories"
                            showClear
                            className="hd-filter-dd"
                            panelClassName="hd-dd-panel"
                        />
                        <button className="hd-btn hd-btn-ghost" onClick={fetchTickets}>
                            <i className="pi pi-refresh" /> Refresh
                        </button>
                    </div>
                </div>

                <DataTable
                    value={tickets}
                    loading={loading}
                    emptyMessage="No tickets found"
                    stripedRows
                    className="hd-datatable"
                    lazy
                    paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalRecords}
                    onPage={(e) => { setFirst(e.first); setRows(e.rows); }}
                    rowsPerPageOptions={[15, 30, 50, 100]}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    currentPageReportTemplate="{first}–{last} of {totalRecords}"
                    onRowClick={(e) => setDetailTicketId(e.data.id)}
                    selectionMode="single"
                >
                    <Column header="Title" body={titleCol} style={{ minWidth: 240 }} />
                    <Column header="Status" body={statusCol} style={{ minWidth: 120 }} />
                    <Column header="Priority" body={priorityCol} style={{ minWidth: 110 }} />
                    <Column header="Category" body={categoryCol} style={{ minWidth: 140 }} />
                    <Column header="Assigned To" body={assignedCol} style={{ minWidth: 160 }} />
                    <Column header="Created" body={createdCol} style={{ minWidth: 170 }} />
                    <Column header="" body={actionsCol} style={{ minWidth: 90 }} />
                </DataTable>
            </div>

            {/* New Ticket Dialog */}
            <AppModal
                header="New Support Ticket"
                visible={createOpen}
                onHide={() => !creating && setCreateOpen(false)}
                footer={dialogFooter}
                panelStyle={{ width: 520 }}
            >
                <div className="hd-form">
                    <div className="hd-form-field">
                        <label className="hd-form-label">Title <span className="hd-req">*</span></label>
                        <InputText
                            value={createForm.title}
                            onChange={e => setCreateField('title', e.target.value)}
                            placeholder="Brief summary of the issue"
                            className={`hd-input${createErrors.title ? ' hd-input-err' : ''}`}
                        />
                        {createErrors.title && <span className="hd-err-msg">{createErrors.title}</span>}
                    </div>

                    <div className="hd-form-field">
                        <label className="hd-form-label">Description</label>
                        <InputTextarea
                            value={createForm.description}
                            onChange={e => setCreateField('description', e.target.value)}
                            placeholder="Add any additional details…"
                            rows={4}
                            className="hd-input"
                        />
                    </div>

                    <div className="hd-form-row">
                        <div className="hd-form-field">
                            <label className="hd-form-label">Priority</label>
                            <Dropdown
                                value={createForm.priority}
                                onChange={e => setCreateField('priority', e.value)}
                                options={PRIORITY_OPTIONS}
                                className="hd-input"
                                panelClassName="hd-dd-panel"
                            />
                        </div>
                        <div className="hd-form-field">
                            <label className="hd-form-label">Category</label>
                            <Dropdown
                                value={createForm.category}
                                onChange={e => setCreateField('category', e.value)}
                                options={CATEGORY_OPTIONS}
                                className="hd-input"
                                panelClassName="hd-dd-panel"
                            />
                        </div>
                    </div>
                </div>
            </AppModal>

            {/* Ticket Detail Dialog */}
            <TicketDetailDialog
                visible={!!detailTicketId}
                ticketId={detailTicketId}
                onHide={() => setDetailTicketId(null)}
                usersList={usersList}
                currentUserId={userId}
                isSuperuser={isSuperuser}
                toast={toast}
                onUpdated={fetchTickets}
            />
        </div>
    );
}
